import { useState, useEffect, useCallback } from 'react';
import { EditIcon, TrashIcon } from 'lucide-react';
import { DataTable, type Column } from '@/components/custom/data-table';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Form } from '@/pages/dashboard/tenant/settings/form';
import { settingsApi } from '@/lib/api';
import { assetsApi } from '@/lib/api/assets';
import type { Setting, SettingCreateInput, SettingUpdateInput } from '@/types/entities';
import { Button } from '@/components/ui/button';
import { getAssetUrl } from '@/lib/collection-field-utils';

interface SettingsTableProps {
  tenantSlug: string;
  canManagePrivateSettings?: boolean;
  showHeader?: boolean;
  headerTitle?: string;
  headerDescription?: string;
}

export function SettingsTable({
  tenantSlug,
  canManagePrivateSettings = false,
}: SettingsTableProps) {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<Setting | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState<Setting | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [assetPathById, setAssetPathById] = useState<Record<number, string>>({});

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await settingsApi.getAllForTenantBySlug(tenantSlug);
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const fetchAssetPaths = async () => {
      try {
        const assets = await assetsApi.getAllByTenantSlug(tenantSlug);
        const nextAssetPathById = assets.reduce<Record<number, string>>((acc, asset) => {
          acc[asset.id] = asset.filepath;
          return acc;
        }, {});
        setAssetPathById(nextAssetPathById);
      } catch {
        setAssetPathById({});
      }
    };

    fetchAssetPaths();
  }, [tenantSlug]);

  const handleCreate = () => {
    setEditingSetting(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = (setting: Setting) => {
    setEditingSetting(setting);
    setIsDialogOpen(true);
  };

  const handleDelete = (setting: Setting) => {
    setSettingToDelete(setting);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!settingToDelete) return;

    try {
      await settingsApi.deleteBySlug(tenantSlug, settingToDelete.id);
      setDeleteDialogOpen(false);
      setSettingToDelete(undefined);
      fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete setting');
    }
  };

  const handleSubmit = async (data: SettingCreateInput | SettingUpdateInput) => {
    if (editingSetting) {
      await settingsApi.updateBySlug(tenantSlug, editingSetting.id, data as SettingUpdateInput);
    } else {
      await settingsApi.createBySlug(tenantSlug, data as SettingCreateInput);
    }
    fetchSettings();
    setIsDialogOpen(false);
    setEditingSetting(undefined);
  };

  const columns: Column<Setting>[] = [
    {
      key: 'id',
      header: 'ID',
      cell: (setting) => <span className="text-muted-foreground">#{setting.id}</span>,
      className: 'w-20',
      sortable: true,
      sortValue: (setting) => setting.id,
    },
    {
      key: 'attribute',
      header: 'Attribute',
      cell: (setting) => (
        <span className="font-mono text-sm">{setting.attribute}</span>
      ),
      sortable: true,
      sortValue: (setting) => setting.attribute,
    },
    {
      key: 'display_name',
      header: 'Display Name',
      cell: (setting) => (
        <span className="text-sm">{setting.display_name || setting.attribute}</span>
      ),
      sortable: true,
      sortValue: (setting) => setting.display_name || setting.attribute,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (setting) => (
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
          {setting.type || 'text'}
        </span>
      ),
      className: 'w-24',
      sortable: true,
      sortValue: (setting) => setting.type || 'text',
    },
    {
      key: 'visibility',
      header: 'Visibility',
      cell: (setting) => (
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
          {setting.visibility}
        </span>
      ),
      className: 'w-28',
      sortable: true,
      sortValue: (setting) => setting.visibility,
    },
    {
      key: 'value',
      header: 'Value',
      cell: (setting) => (
        setting.type === 'image' ? (() => {
          const assetId = Number(setting.value);
          if (!Number.isFinite(assetId) || assetId <= 0) {
            return <span className="text-sm text-muted-foreground">No image</span>;
          }

          const assetUrl = getAssetUrl(tenantSlug, assetId);
          const assetPath = assetPathById[assetId];

          return (
            <div className="flex min-w-0 items-center gap-2">
              <img
                src={assetUrl}
                alt={`Asset ${assetId}`}
                className="h-9 w-12 shrink-0 rounded border bg-muted/30 object-cover"
              />
              <span className="truncate font-mono text-xs text-muted-foreground">{assetPath ?? `Asset #${assetId}`}</span>
            </div>
          );
        })() : (
          <span className="text-sm text-muted-foreground truncate max-w-xs block">
            {setting.value}
          </span>
        )
      ),
      sortable: true,
      sortValue: (setting) => {
        if (setting.type !== 'image') {
          return setting.value;
        }

        const assetId = Number(setting.value);
        if (!Number.isFinite(assetId) || assetId <= 0) {
          return '';
        }

        return assetPathById[assetId] ?? `asset-${assetId}`;
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (setting) => new Date(setting.created_at).toLocaleDateString(),
      className: 'w-32',
      sortable: true,
      sortValue: (setting) => new Date(setting.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      cell: (setting) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(setting)}
            className="h-8 w-8"
          >
            <EditIcon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(setting)}
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
      className: 'w-24',
    },
  ];

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive p-3 rounded-md">
          {error}
        </div>
      )}

      <DataTable
        data={settings}
        columns={columns}
        searchKeys={['attribute', 'display_name', 'value', 'type', 'visibility']}
        searchPlaceholder="Search settings..."
        emptyMessage="No settings found"
        isLoading={isLoading}
        onCreate={handleCreate}
        createLabel={'Add Record'}
      />

      <Form
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsDialogOpen(false);
          setEditingSetting(undefined);
        }}
        setting={editingSetting}
        tenantSlug={tenantSlug}
        canManagePrivateSettings={canManagePrivateSettings}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Setting"
        description={`Are you sure you want to delete "${settingToDelete?.attribute}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </div>
  );
}
